import React from 'react';
import './Policy.css';

const RefundPolicy = () => {
  return (
    <div className="policy-page">
      <div className="container">
        <div className="policy-content">
          <h1>Refund Policy</h1>
          <p className="last-updated">Last Updated: October 8, 2025</p>

          <section>
            <h2>1. General Refund Policy</h2>
            <p>Land Tracker is committed to customer satisfaction. We offer refunds under certain conditions as outlined in this policy. All refund requests are subject to review and approval.</p>
          </section>

          <section>
            <h2>2. Refund Eligibility</h2>
            <h3>You may be eligible for a refund if:</h3>
            <ul>
              <li>You were charged incorrectly or multiple times due to a technical error</li>
              <li>The app has critical bugs that prevent you from using core features (after we've attempted to resolve the issue)</li>
              <li>You cancelled within the first 48 hours of a new subscription (not applicable to renewals)</li>
              <li>You were charged after cancelling your subscription (processing delays excluded)</li>
            </ul>

            <h3>You are NOT eligible for a refund if:</h3>
            <ul>
              <li>You simply changed your mind about the subscription</li>
              <li>You forgot to cancel before the renewal date</li>
              <li>You didn't use the app during the subscription period</li>
              <li>You violated our Terms of Use</li>
              <li>Your account was suspended or terminated for policy violations</li>
            </ul>
          </section>

          <section>
            <h2>3. Refund Timeframes</h2>
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Refund Period</th>
                  <th>Refund Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>First-time subscription</td>
                  <td>Within 48 hours</td>
                  <td>Full refund</td>
                </tr>
                <tr>
                  <td>Technical error/Double charge</td>
                  <td>Within 30 days</td>
                  <td>Full refund</td>
                </tr>
                <tr>
                  <td>Critical bug (unresolved)</td>
                  <td>Within 14 days</td>
                  <td>Prorated refund</td>
                </tr>
                <tr>
                  <td>Subscription renewal</td>
                  <td>Not eligible</td>
                  <td>No refund</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h2>4. How to Request a Refund</h2>
            <p>To request a refund, follow these steps:</p>
            <ol>
              <li>Contact our support team at <strong>refunds@landtracker.app</strong></li>
              <li>Include your account email, subscription details, and reason for refund request</li>
              <li>Provide any relevant screenshots or evidence (for technical issues)</li>
              <li>Wait for our team to review your request (typically within 2-3 business days)</li>
            </ol>
            <div className="highlight-box">
              <p><strong>Important:</strong> Refund requests submitted through app stores (Apple App Store or Google Play Store) are subject to their respective refund policies, which may differ from ours.</p>
            </div>
          </section>

          <section>
            <h2>5. App Store Refund Policies</h2>
            <h3>Apple App Store</h3>
            <p>For subscriptions purchased through the Apple App Store:</p>
            <ul>
              <li>Apple handles all refund requests</li>
              <li>Visit <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a></li>
              <li>Apple's refund policy applies</li>
            </ul>

            <h3>Google Play Store</h3>
            <p>For subscriptions purchased through Google Play:</p>
            <ul>
              <li>Google handles refund requests within 48 hours of purchase</li>
              <li>Visit <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noopener noreferrer">Google Play Subscriptions</a></li>
              <li>After 48 hours, contact us directly</li>
            </ul>
          </section>

          <section>
            <h2>6. Refund Processing</h2>
            <p>Once your refund is approved:</p>
            <ul>
              <li><strong>Processing Time:</strong> 5-10 business days</li>
              <li><strong>Method:</strong> Refunded to original payment method</li>
              <li><strong>Notification:</strong> You'll receive an email confirmation</li>
              <li><strong>Access:</strong> Premium features will be disabled immediately upon refund</li>
            </ul>
          </section>

          <section>
            <h2>7. Partial Refunds</h2>
            <p>In some cases, we may offer a partial (prorated) refund based on:</p>
            <ul>
              <li>The amount of time remaining in your subscription period</li>
              <li>The severity of the issue encountered</li>
              <li>Your usage history and subscription tenure</li>
            </ul>
          </section>

          <section>
            <h2>8. Free Trial Refunds</h2>
            <p>Free trials are not eligible for refunds as no payment has been made. However, you can cancel anytime during the trial to avoid being charged.</p>
          </section>

          <section>
            <h2>9. Dispute Resolution</h2>
            <p>If your refund request is denied and you disagree with our decision:</p>
            <ol>
              <li>Reply to the refund decision email with additional information</li>
              <li>Request escalation to our senior support team</li>
              <li>We'll conduct a final review within 5 business days</li>
            </ol>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>For refund inquiries or questions about this policy:</p>
            <p><strong>Email:</strong> refunds@landtracker.app</p>
            <p><strong>Support:</strong> info@landtracker.app</p>
            <p><strong>Response Time:</strong> Within 24-48 hours</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
